import React, { forwardRef } from 'react';
import '../../styles/vendorregistration.css';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

const AccountDetailsPage = forwardRef((props, ref) => {
  const {
    formData,
    updateFormData,
    errors,
    handleFieldFocus,
    registerFieldRef,
    paymentOptions,
    currencyOptions,
    handleFileUpload,
    idCardTypes
  } = props;

  const { t } = useTranslation();

  // Convert idCardTypes to react-select format
  const idCardTypeOptions = idCardTypes.map(type => ({
    value: type.value,
    label: type.label
  }));

  // Get current selected value for react-select
  const selectedIdCardType = formData.accountDetails.idCardType
    ? idCardTypeOptions.find(option => option.value === formData.accountDetails.idCardType)
    : null;

  const handleChange = (e) => {
    const { id, value } = e.target;
    updateFormData('accountDetails', id, value);
  };

  // FIXED: Proper React Select handler for ID card type
  const handleIdCardTypeChange = (option) => {
    const value = option ? option.value : '';
    updateFormData('accountDetails', 'idCardType', value);
    // Clear ID card number when type changes
    updateFormData('accountDetails', 'idCardNo', '');
    handleFieldFocus('accountDetails', 'idCardType');
  };

  const handleIdCardNoChange = (e) => {
    let value = e.target.value;
    const idCardType = formData.accountDetails.idCardType;
    const idTypeConfig = idCardTypes.find(type => type.value === idCardType);

    if (idTypeConfig) {
      // Apply pattern-specific validation
      if (idCardType === 'PE') {
        // Allow only alphanumeric for PE type
        value = value.replace(/[^A-Za-z0-9]/g, '');
      } else if (idCardType === 'COF') {
        // Allow any characters for COF
        // No filtering needed
      } else {
        // For other types, allow only numbers
        value = value.replace(/\D/g, '');
      }

      // Apply max length restriction
      if (value.length > idTypeConfig.maxLength) {
        value = value.slice(0, idTypeConfig.maxLength);
      }
    }

    updateFormData('accountDetails', 'idCardNo', value);

    // Validate ID card number on change with detailed error messages
    if (value && idCardType) {
      validateIdCardNo(value, idCardType);
    } else {
      handleFieldFocus('accountDetails', 'idCardNo');
    }
  };

  // Enhanced validation function with detailed error messages
  const validateIdCardNo = (value, idCardType) => {
    if (!idCardType) return;

    const idTypeConfig = idCardTypes.find(type => type.value === idCardType);
    if (!idTypeConfig) return;

    let error = null;

    // Check for starting with zero for CF and DIMEX
    if ((idCardType === 'CF' || idCardType === 'DIMEX') && value.startsWith('0')) {
      error = 'idCardStartsWithZero';
    }
    // Check minimum length
    else if (value.length < idTypeConfig.minLength) {
      error = 'invalidIdCardLength';
    }
    // Check pattern validation
    else if (idTypeConfig.pattern && !idTypeConfig.pattern.test(value)) {
      error = 'invalidIdCardPattern';
    }

    if (error) {
      const newErrors = {
        ...errors,
        accountDetails: {
          ...errors.accountDetails,
          idCardNo: error
        }
      };
    } else {
      handleFieldFocus('accountDetails', 'idCardNo');
    }
  };

  // Enhanced error message function
  const getErrorMessage = (errorKey, field) => {
    if (field === 'idCardNo') {
      switch (errorKey) {
        case 'idCardStartsWithZero':
          return t('vendorRegistration.errors.idCardStartsWithZero');
        case 'invalidIdCardLength':
          return t('vendorRegistration.errors.invalidIdCardLength');
        case 'invalidIdCardPattern':
          return t('vendorRegistration.errors.invalidIdCardPattern');
        case 'idCardAlreadyExists':
          return t('vendorRegistration.errors.idCardAlreadyExists');
        case 'invalidIdCard':
        default:
          return t('vendorRegistration.errors.invalidIdCard');
      }
    }
    return t(`vendorRegistration.errors.${errorKey}`);
  };

  const truncateFileName = (name, maxLength) => {
    if (name.length <= maxLength) return name;
    return `${name.substring(0, maxLength)}...`;
  };

  const getPlaceholder = () => {
    const idCardType = formData.accountDetails.idCardType;
    const idTypeConfig = idCardTypes.find(type => type.value === idCardType);
    return idTypeConfig ? t(idTypeConfig.description) : t('vendorRegistration.idCard.enter_number');
  };

  return (
    <div ref={ref} className="form-section">
      <h2 className="page-title">{t('vendorRegistration.headers.step5')}</h2>
      <p className="page-description">{t('vendorRegistration.subdescriptions.step5')}</p>
      <div className='form-grid'>
        <div className="form-group">
          <label htmlFor="accountname">{t('vendorRegistration.account.accountHolder')}</label>
          <div className="input-container">
            <input
              type="text"
              id="accountname"
              maxLength="200"
              placeholder={t('vendorRegistration.account.accountHolder')}
              value={formData.accountDetails.accountname || ''}
              onChange={(e) => { handleChange(e); handleFieldFocus('accountDetails', 'accountname') }}
              ref={(el) => registerFieldRef('accountname', el)}
              className={errors.accountDetails?.accountname ? 'error' : ''}
            />
            {errors.accountDetails?.accountname && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.accountname}`)}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="bankname">{t('vendorRegistration.account.bankName')}</label>
          <div className="input-container">
            <input
              type="text"
              id="bankname"
              placeholder={t('vendorRegistration.account.bankName')}
              maxLength="50"
              value={formData.accountDetails.bankname || ''}
              onChange={(e) => { handleChange(e); handleFieldFocus('accountDetails', 'bankname') }}
              ref={(el) => registerFieldRef('bankname', el)}
              className={errors.accountDetails?.bankname ? 'error' : ''}
            />
            {errors.accountDetails?.bankname && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.bankname}`)}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="iban">{t('vendorRegistration.account.iban')}</label>
          <div className="input-container">
            <input
              type="text"
              id="iban"
              maxLength="50"
              placeholder={t('vendorRegistration.account.iban')}
              value={formData.accountDetails.iban || ''}
              onChange={(e) => { handleChange(e); handleFieldFocus('accountDetails', 'iban') }}
              ref={(el) => registerFieldRef('iban', el)}
              className={errors.accountDetails?.iban ? 'error' : ''}
            />
            {errors.accountDetails?.iban && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.iban}`)}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="swift">{t('vendorRegistration.account.swift')}</label>
          <div className="input-container">
            <input
              type="text"
              id="swift"
              maxLength="50"
              placeholder={t('vendorRegistration.account.swift')}
              value={formData.accountDetails.swift || ''}
              onChange={(e) => { handleChange(e); handleFieldFocus('accountDetails', 'swift') }}
              ref={(el) => registerFieldRef('swift', el)}
              className={errors.accountDetails?.swift ? 'error' : ''}
            />
            {errors.accountDetails?.swift && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.swift}`)}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address">{t('vendorRegistration.account.branchAddress')}</label>
          <div className="input-container">
            <input
              type="text"
              id="address"
              maxLength="50"
              placeholder={t('vendorRegistration.account.branchAddress')}
              value={formData.accountDetails.address || ''}
              onChange={(e) => { handleChange(e); handleFieldFocus('accountDetails', 'address') }}
              ref={(el) => registerFieldRef('address', el)}
              className={errors.accountDetails?.address ? 'error' : ''}
            />
            {errors.accountDetails?.address && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.address}`)}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="payment_method">{t('vendorRegistration.account.paymentMethod')}</label>
          <div className="input-container">
            <Select
              id="payment_method"
              className={`dropdown ${errors.accountDetails?.payment_method ? 'error' : ''}`}
              onFocus={() => handleFieldFocus('accountDetails', 'payment_method')}
              ref={(el) => registerFieldRef('payment_method', el)}
              onChange={(option) => updateFormData('accountDetails', 'payment_method', option)}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '47px',
                  border: '1.5px solid #d1d5db54',
                  '&:hover': {
                    border: '1px solid #65c7c2; !important',
                  },
                  ...(state.isFocused && {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  }),
                }),
                menu: (provided) => ({
                  ...provided,
                  maxHeight: '200px !important',
                  zIndex: 100,
                }),
              }}
              value={formData.accountDetails.payment_method}
              options={paymentOptions}
              placeholder={t('vendorRegistration.common.selectOption')} />
          </div>
          {errors.accountDetails?.payment_method && (
            <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.payment_method}`)}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="currency">{t('vendorRegistration.account.currency')}</label>
          <div className="input-container">
            <Select
              id="currency"
              className={`dropdown ${errors.accountDetails?.currency ? 'error' : ''}`}
              onFocus={() => handleFieldFocus('accountDetails', 'currency')}
              ref={(el) => registerFieldRef('currency', el)}
              onChange={(option) => updateFormData('accountDetails', 'currency', option)}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  height: '47px',
                  border: '1.5px solid #d1d5db54',
                  '&:hover': {
                    border: '1px solid #65c7c2; !important',
                  },
                  ...(state.isFocused && {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  }),
                }),
                menu: (provided) => ({
                  ...provided,
                  maxHeight: '200px !important',
                  zIndex: 100,
                }),
              }}
              value={formData.accountDetails.currency}
              options={currencyOptions}
              placeholder={t('vendorRegistration.common.selectOption')}
            />
          </div>
          {errors.accountDetails?.currency && (
            <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.currency}`)}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="proof">{t('vendorRegistration.account.uploadProof')}</label>
          <div
            className="file-upload-container"
            onClick={() => document.getElementById('proof').click()}
            style={{ minHeight: '47px', padding: '10px' }}
          >
            <input
              type="file"
              id="proof"
              className="file-input-hidden"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileUpload}
            />

            <div className="file-list">
              {formData.accountDetails.proof?.length > 0 ? (
                formData.accountDetails.proof.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">
                      {truncateFileName(file.name, 20)}
                    </span>
                    <button
                      type="button"
                      className="file-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedFiles = [...formData.accountDetails.proof];
                        updatedFiles.splice(index, 1);
                        updateFormData('accountDetails', 'proof', updatedFiles);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <span className="file-placeholder">
                  {t('vendorRegistration.account.uploadFile')}
                </span>
              )}
            </div>

            <svg
              className="upload-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
            >
              <path
                fill="currentColor"
                d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16h-2Zm-7 4v-5h2v3h12v-3h2v5H4Z"
              />
            </svg>
          </div>
          {errors.accountDetails?.proof && (
            <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.proof}`)}</div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="idCardType">{t('vendorRegistration.idCard.idcard_type')}</label>
          <div className="input-container">
            <Select
              id="idCardType"
              className={`dropdown ${errors.accountDetails?.idCardType ? 'error' : ''}`}
              onFocus={() => handleFieldFocus('accountDetails', 'idCardType')}
              ref={(el) => registerFieldRef('idCardType', el)}
              onChange={handleIdCardTypeChange}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  height: '47px',
                  border: errors.accountDetails?.idCardType ? '1px solid #e74c3c !important' : '1.5px solid #d1d5db54',
                  '&:hover': {
                    border: errors.accountDetails?.idCardType ? '1px solid #e74c3c !important' : '1px solid #65c7c2 !important',
                  },
                  ...(state.isFocused && {
                    border: errors.accountDetails?.idCardType ? '1px solid #e74c3c !important' : '1px solid #65c7c2 !important',
                    boxShadow: 'none !important',
                  }),
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
                menu: (provided) => ({
                  ...provided,
                  maxHeight: '250px',
                  overflow: 'auto',
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: '250px',
                  padding: 0,
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#65c7c2' : state.isFocused ? '#f0f9f8' : 'white',
                  color: state.isSelected ? 'white' : 'inherit',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#65c7c2' : '#f0f9f8',
                  },
                  fontSize: '14px',
                  padding: '8px 12px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: 'inherit',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#9ca3af',
                }),
              }}
              value={selectedIdCardType}
              options={idCardTypeOptions}
              placeholder={t('vendorRegistration.common.selectOption')}
              isClearable={true}
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="absolute"
              menuPlacement="auto"
            />
            {errors.accountDetails?.idCardType && (
              <div className="error-message">{t(`vendorRegistration.errors.${errors.accountDetails?.idCardType}`)}</div>
            )}
          </div>
        </div>

        {/* ID Card Number Field */}
        <div className="form-group">
          <label htmlFor="idCardNo">{t('vendorRegistration.idCard.idcard_no')}</label>
          <div className="input-container">
            <input
              type="text"
              id="idCardNo"
              placeholder={getPlaceholder()}
              value={formData.accountDetails.idCardNo || ''}
              onChange={handleIdCardNoChange}
              ref={(el) => registerFieldRef('idCardNo', el)}
              className={errors.accountDetails?.idCardNo ? 'error' : ''}
              disabled={!formData.accountDetails.idCardType}
              style={{
                width: '100%',
                opacity: !formData.accountDetails.idCardType ? 0.6 : 1
              }}
            />
            {errors.accountDetails?.idCardNo && (
              <div className="error-message">
                {getErrorMessage(errors.accountDetails?.idCardNo, 'idCardNo')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default AccountDetailsPage;